import { Box, Divider, Flex, Price, PrimaryButton, Text } from "flicket-ui";
import { useCallback, useContext, useMemo, useState } from "react";
import {
  BaseContext,
  ReservationActionTypes,
  ReservationContext,
} from "~context";
import BuyNowPayLater, {
  AfterpayTeaser,
} from "~features/seated-reservation/components/common/common.BuyNowPayLater";
import { MembershipQuery, IntegrationType } from "~graphql/sdk";
import { useAccount, useOrganization } from "~hooks";
import { OrganizationFeatures } from "~lib";
import { AddonListItem, ListItem } from "./item";
import { Option } from "~components/common";
import { SeatsIOSeat } from "../constants";
import { QuantitySetValidationModal } from "../common/common.QuantitySetValidation.modal";
import { PaymentPlanBadge } from "~features/seated-reservation/components/common/common.PaymentPlanBadge";
import { Cart } from "~features/checkout/cart";
import { getAfterPayLocaleByCurrency } from "~config";

type QuantitySetValidation = {
  count: number;
  quantitySet: number;
  valid: boolean;
  name: string;
};

type PricingProps = {
  addons?: any[];
  changingSeatsFee?: number;
  changeTickets: any[];
  navigate: () => void;
  isNavigating?: boolean;
  getPrice: (seat: SeatsIOSeat) => [number, number];
  noSeatsAvailable: boolean;
  onBack: () => void;
  priceComp: number | undefined;
  validSeatSelection: boolean;
  paymentPlanSettings: MembershipQuery["membership"]["paymentPlanSettings"];
  fees: MembershipQuery["membership"]["fees"];
  multiBuyPromotions: MembershipQuery["membership"]["multiBuyPromotions"];
};

export const Pricing = ({
  addons: addonOptions,
  changingSeatsFee,
  changeTickets,
  navigate,
  isNavigating,
  getPrice,
  noSeatsAvailable,
  onBack,
  priceComp,
  validSeatSelection,
  paymentPlanSettings,
  fees,
  multiBuyPromotions,
}: PricingProps) => {
  const { organization, t, hasFeature } = useOrganization();

  const { seats, addons, dispatch, availableMembershipTypes } = useContext(
    ReservationContext
  );

  const [quantitySetValidationError, setQuantitySetValidationError] = useState<{
    name: string;
    quantitySet: number;
  } | null>(null);

  const { isPOS, isAdmin } = useAccount();
  const { isSinglePage } = useContext(BaseContext);

  const showHiddenFees = hasFeature(OrganizationFeatures.ShowHiddenFees);

  const ticketingOptions: Option[] = availableMembershipTypes.map(
    ({ id, name, ...rest }) => ({
      label: name,
      value: id,
    })
  );

  const availableAddons = useMemo(
    () => addonOptions?.filter(({ isActive }) => isActive),
    [addonOptions]
  );

  const cart = new Cart(fees, multiBuyPromotions);
  cart.isPos(isPOS);
  if (!hasFeature(OrganizationFeatures.OrderSmsConfirmationDisabled)) {
    cart.sendSmsConfirmation(!(isAdmin || isPOS));
  }
  cart.includeFees(showHiddenFees);

  seats?.forEach((seat) => {
    const [price] = getPrice(seat);
    seat.ticketType.fee = cart.itemFee(seat.ticketType.id, price);

    cart.add(seat.ticketType.id, price);
  });

  availableAddons?.forEach((addon) => {
    const quantity = addons?.[addon.id]?.value;

    if (quantity) {
      cart.add(addon.id, addon.price, quantity);
    }
  });

  const updateSeat = useCallback(
    (id, ticketId) => {
      const ticketType = availableMembershipTypes.find(
        (v) => v.id === ticketId
      );

      dispatch({
        type: ReservationActionTypes.SET_SEAT_TYPE,
        payload: { id, ticketType },
      });
    },
    [dispatch]
  );

  const updateAddon = useCallback(
    (id, quantity) =>
      dispatch({
        type: ReservationActionTypes.UPDATE_ADDON,
        payload: { id, quantity },
      }),
    [dispatch]
  );

  let pricingOptions;

  if (seats?.length) {
    pricingOptions = seats?.map((seat, i) => (
      <ListItem
        seat={seat}
        handleTicketTypeChange={updateSeat}
        options={ticketingOptions}
        getPrice={getPrice}
        key={i}
        showHiddenFees={showHiddenFees}
      />
    ));
  } else {
    pricingOptions = <Text mb={3}>Please select your seating</Text>;
  }

  const onCloseQuantitySetValidationModal = () => {
    setQuantitySetValidationError(null);
  };

  const validateQuantitySets = (
    seats: SeatsIOSeat[],
    setQuantitySetValidationError: (error: {
      name: string;
      quantitySet: number;
    }) => void
  ): boolean => {
    const validationMap = new Map<string, QuantitySetValidation>();

    for (const { ticketType } of seats) {
      if (!ticketType?.quantitySet) continue;

      const currentEntry = validationMap.get(ticketType.id);
      const currentCount = currentEntry?.count || 0;
      const newCount = currentCount + 1;
      const isValid = newCount % ticketType.quantitySet === 0;

      validationMap.set(ticketType.id, {
        count: newCount,
        valid: isValid,
        quantitySet: ticketType.quantitySet,
        name: ticketType.name,
      });
    }

    for (const entry of validationMap.values()) {
      if (!entry.valid) {
        setQuantitySetValidationError({
          name: entry.name,
          quantitySet: entry.quantitySet,
        });
        return false;
      }
    }

    return true;
  };

  const handleBuyButtonClicked = () => {
    if (!seats) return;

    const isValid = validateQuantitySets(seats, setQuantitySetValidationError);

    if (isValid) {
      return navigate();
    }
  };

  return (
    <Box>
      <QuantitySetValidationModal
        isOpen={!!quantitySetValidationError}
        onClose={onCloseQuantitySetValidationModal}
        quantitySet={quantitySetValidationError?.quantitySet}
        name={quantitySetValidationError?.name}
      />
      <Text fontWeight="heavy" fontSize={6} color="N800" lineHeight="normal">
        Your selection
      </Text>

      <Divider my={2} />

      {pricingOptions}

      {!noSeatsAvailable && seats?.length > 0 && (
        <>
          {!!availableAddons?.length && (
            <Box mb={2} mt={4}>
              <Text
                fontWeight="heavy"
                fontSize={6}
                color="N800"
                lineHeight="normal"
              >
                Available Addons
              </Text>
              <Divider my={2} />
              {availableAddons.map(({ id, name, price, ...rest }) => (
                <AddonListItem
                  {...rest}
                  id={id}
                  key={id}
                  name={name}
                  price={price}
                  quantity={addons?.[id] || 0}
                  onChange={(value) => {
                    updateAddon(id, value);
                  }}
                />
              ))}
            </Box>
          )}

          {!!changeTickets?.length && (
            <Box mb={2}>
              <Divider />
              {!!changingSeatsFee && (
                <Flex mt={2} justifyContent="space-between" w="100%">
                  <Text fontWeight="extraBold" fontSize={3} color="N700">
                    Reservation change fee
                  </Text>
                  <Text fontWeight="medium" fontSize={2} color="N600">
                    <Price price={changingSeatsFee} />
                  </Text>
                </Flex>
              )}
              {!!priceComp && (
                <Flex mt={2} justifyContent="space-between" w="100%">
                  <Text fontWeight="extraBold" fontSize={3} color="N700">
                    Price difference compensation
                  </Text>
                  <Text fontWeight="medium" fontSize={2} color="N600">
                    <Price price={-priceComp} />
                  </Text>
                </Flex>
              )}
            </Box>
          )}

          <Divider />

          <Flex mt={2} justifyContent="space-between" w="100%">
            <Text fontWeight="medium" fontSize={2} color="N600">
              Total tickets
            </Text>
            <Text
              fontWeight="medium"
              fontSize={4}
              lineHeight={"18px" as any}
              color={"black" as any}
            >
              {seats?.length ?? 0}
            </Text>
          </Flex>

          {cart.multibuyDiscount > 0 && (
            <>
              <Flex mt={2} justifyContent="space-between" w="100%">
                <Text fontWeight="medium" fontSize={2} color="N600">
                  Multibuy promotion applied
                </Text>
                <Price
                  price={-cart.multibuyDiscount}
                  fontWeight="medium"
                  fontSize={4}
                  lineHeight={"18px" as any}
                  color={"black" as any}
                />
              </Flex>
              <Divider my={{ _: 3, md: 2 }} />
            </>
          )}

          <Flex mt={2} justifyContent="space-between" w="100%">
            <Text fontWeight="medium" fontSize={2} color="N600">
              Total {showHiddenFees ? "(including booking fees)" : ""}
            </Text>
            <Text
              fontWeight="medium"
              fontSize={4}
              lineHeight={"18px" as any}
              color={"black" as any}
            >
              {seats ? <Price price={cart.total} /> : "-"}
            </Text>
          </Flex>

          {!isPOS && cart.total > 0 && (
            <>
              {cart.afterPay > 0 && (
                <Box mt={2} w="100%">
                  <BuyNowPayLater
                    totalPrice={cart.afterPay}
                    hasTicketSelected={!!seats}
                    isSmallDisplay={true}
                    bnplIntegration={{
                      type: IntegrationType.PaymentAfterpay,
                    }}
                  />
                </Box>
              )}
              {cart.laybuy > 0 && (
                <Box mt={2} w="100%">
                  <BuyNowPayLater
                    totalPrice={cart.laybuy}
                    hasTicketSelected={!!seats}
                    isSmallDisplay={true}
                    bnplIntegration={{
                      type: IntegrationType.PaymentLaybuy,
                    }}
                  />
                </Box>
              )}
              {cart.stripeAfterPay > 0 && (
                <Box mt={2}>
                  <AfterpayTeaser
                    locale={getAfterPayLocaleByCurrency(organization.currency)}
                    currency={organization.currency ?? "NZD"}
                    amount={cart.stripeAfterPay}
                  />
                </Box>
              )}
            </>
          )}

          {paymentPlanSettings && !paymentPlanSettings.disabledAt && (
            <PaymentPlanBadge
              maxInstallmentCount={paymentPlanSettings?.scheduleOptions
                ?.map((s: { installmentCount: number }) => s?.installmentCount)
                ?.reduce((a, b) => Math.max(a, b))}
            />
          )}

          <PrimaryButton
            boxShadow="button"
            mt={{ _: "6/4", md: 4 }}
            onClick={handleBuyButtonClicked}
            isLoading={isNavigating}
            disabled={
              !seats?.length ||
              (validSeatSelection === false && !isAdmin && !isPOS)
            }
            w="100%"
            fontSize={4}
          >
            {validSeatSelection === true || isAdmin || isPOS
              ? isSinglePage
                ? "Buy tickets"
                : `Buy ${t("membership_name_plural").toLowerCase()}`
              : "Can not leave a single seat"}
          </PrimaryButton>
        </>
      )}
    </Box>
  );
};
